import { ConfigSettings } from "../config/Config";
import { authService } from "./AuthService";
import { showError } from "./ModalService";
import i18next from "i18next";
import { addGlobalSpinner, removeGlobalSpinner } from "./GlobalSpinner";

const settings = ConfigSettings();

let RequestCounts = 0;
function addSpinner() {
  if (RequestCounts < 1) addGlobalSpinner();
  RequestCounts++;
}
function removeSpinner() {
  RequestCounts--;
  if (RequestCounts <= 0) removeGlobalSpinner();
}

async function handleResponse(response, errorHandler) {
  if (response.ok) {
    return response.json();
  } else if (response.status === 401) {
    return authService.refresh();
  } else {
    if (errorHandler) {
      errorHandler(response);
    } else {
      const error = await response.json();
      showError(i18next.t(`translation:${error.message.text}`));
    }
    return { error: true };
  }
}

export async function init(url, token) {
  addSpinner();
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    }
  };
  return await fetch(url, requestOptions)
    .then(async (res) => {
      const data = await res.json();
      if (res.ok) {
        return data;
      } else {
        showError(i18next.t(`translation:${data.message.text}`));

        throw new Error(data.message.text);
      }
    })

    .finally(() => {
      removeSpinner();
    });
}

export async function Request(url, method, data, errorHandler) {
  addSpinner();
  let tokeen = await getTokenApi();
  let company = document.getElementById("current_company_id").value;

  return await fetch(settings.resourceServer.apiUrl + url, {
    method: method,
    body: data ? JSON.stringify(data) : null,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${tokeen}`,
      AppLang: i18next.language,
      CurrentCompany: company
    }
  })
    .then((res) => handleResponse(res, errorHandler))

    .catch((err) => console.error(err))
    .finally(() => {
      removeSpinner();
    });
}
export async function AnonymusRequest(url, method, data, errorHandler) {
  addSpinner();

  return await fetch(settings.resourceServer.apiUrl + url, {
    method: method,
    body: data ? JSON.stringify(data) : null,
    headers: {
      "content-type": "application/json",

      AppLang: i18next.language
    }
  })
    .then((res) => handleResponse(res, errorHandler))
    .catch((err) => console.error(err))
    .finally(() => {
      removeSpinner();
    });
}

export function RequestFile(url, file) {
  addSpinner();
  return fetch(settings.resourceServer.apiUrl + url, {
    method: "POST",
    body: file,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("@vitae/internal_token")}`
    }
  })
    .then(handleResponse)
    .catch((err) => console.error(err))
    .finally(() => {
      removeSpinner();
    });
}

export function DownloadFile(url, method, data) {
  addSpinner();
  return fetch(settings.resourceServer.apiUrl + url, {
    method: method ? method : "GET",
    body: data ? JSON.stringify(data) : null,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("@vitae/internal_token")}`,
      "content-type": "application/json"
    }
  })
    .catch((resp) => {
      const error = resp.json();
      showError(i18next.t(`translation:${error.message.text}`));
    })
    .finally(() => {
      removeSpinner();
    });
}

export const openDownloadedFileInNewTab = (blob, fileName) => {
  const file = new File([blob], `${fileName}.pdf`, {
    type: "application/pdf"
  });
  const url = window.URL.createObjectURL(file);
  window.open(url, "_blank");
};

/**
 * Streaming request for real-time candidate recommendations
 * @param {string} url - The API endpoint URL
 * @param {Object} data - Request payload
 * @param {Function} onData - Callback for each streaming data chunk
 * @param {Function} onComplete - Callback when streaming completes
 * @param {Function} onError - Callback for errors
 * @param {AbortController} abortController - Optional abort controller for cancellation
 * @returns {Promise} - Promise that resolves when stream starts
 */
export async function StreamingRequest(
  url,
  data,
  onData,
  onComplete,
  onError,
  abortController
) {
  addSpinner();

  try {
    const token = await getTokenApi();
    const company = document.getElementById("current_company_id").value;

    const response = await fetch(settings.resourceServer.apiUrl + url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
        AppLang: i18next.language,
        CurrentCompany: company
      },
      body: JSON.stringify(data),
      signal: abortController?.signal
    });

    if (!response.ok) {
      if (response.status === 401) {
        await authService.refresh();
        throw new Error("Authentication expired. Please try again.");
      } else {
        const error = await response.json();
        throw new Error(
          error.message?.text ||
            `HTTP ${response.status}: ${response.statusText}`
        );
      }
    }

    // Start processing the stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.substring(6).trim();
            if (data) {
              try {
                const parsed = JSON.parse(data);

                if (parsed.type === "complete") {
                  onComplete?.();
                  return;
                } else if (parsed.error) {
                  onError?.(new Error(parsed.error));
                  return;
                } else {
                  // New candidate data received
                  onData?.(parsed);
                }
              } catch (parseError) {
                console.warn(
                  "Failed to parse streaming data:",
                  data,
                  parseError
                );
                // Continue processing other lines
              }
            }
          }
        }
      }

      // Stream ended naturally
      onComplete?.();
    } catch (streamError) {
      if (streamError.name === "AbortError") {
        console.log("Stream aborted by user");
        onComplete?.();
      } else {
        onError?.(streamError);
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    console.error("Streaming request error:", error);
    onError?.(error);
  } finally {
    removeSpinner();
  }
}

function getTokenApi() {
  let token = localStorage.getItem("@vitae/internal_token");

  if (token) {
    return token;
  } else {
    return authService.refresh();
  }
}
